(() => {
  const initDrawerLevels = (root = document) => {
    const headers = [];

    if (root instanceof Element && root.matches('[data-kv-header-section]')) headers.push(root);
    root.querySelectorAll?.('[data-kv-header-section]').forEach((header) => headers.push(header));

    headers.forEach((header) => {
      const menu = header.querySelector('[data-kv-mobile-menu]');
      const drawer = header.querySelector('[data-kv-mobile-drawer]');
      const shopTrigger = header.querySelector('[data-kv-mobile-menu-shop]');
      const shopBack = header.querySelector('[data-kv-mobile-menu-back]');
      const tertiaryBack = header.querySelector('[data-kv-mobile-tertiary-back]');
      const tertiaryBackLabel = header.querySelector('[data-kv-mobile-tertiary-back-label]');
      const primaryPage = header.querySelector('[data-kv-menu-page="primary"]');
      const shopPage = header.querySelector('[data-kv-menu-page="shop"]');
      const tertiaryPage = header.querySelector('[data-kv-menu-page="tertiary"]');
      const tertiaryTriggers = Array.from(header.querySelectorAll('[data-kv-mobile-tertiary-trigger]'));
      const tertiaryGroups = Array.from(header.querySelectorAll('[data-kv-mobile-tertiary-group]'));

      if (!menu || !drawer || !shopTrigger || !shopBack || !primaryPage || !shopPage) {
        return;
      }

      const hasTertiary =
        Boolean(tertiaryBack) &&
        Boolean(tertiaryBackLabel) &&
        Boolean(tertiaryPage) &&
        tertiaryTriggers.length > 0 &&
        tertiaryGroups.length > 0;

      if (drawer.dataset.kvLevelsReady === 'true') return;
      drawer.dataset.kvLevelsReady = 'true';

      const LEVEL_OVERLAP_MS = 220;
      const LEVEL_SWITCH_MS = 640;
      const LEVEL_FOCUS_MS = 720;

      let overlapTimer = 0;
      let switchTimer = 0;
      let focusTimer = 0;
      let activeTertiaryKey = '';

      const clearTimers = () => {
        window.clearTimeout(overlapTimer);
        window.clearTimeout(switchTimer);
        window.clearTimeout(focusTimer);
        overlapTimer = 0;
        switchTimer = 0;
        focusTimer = 0;
      };

      const restartReveal = (className) => {
        drawer.classList.remove('is-primary-revealing', 'is-shop-revealing', 'is-tertiary-revealing');
        void drawer.offsetWidth;
        drawer.classList.add(className);
      };

      const setPageAria = (pageName) => {
        primaryPage.setAttribute('aria-hidden', String(pageName !== 'primary'));
        shopPage.setAttribute('aria-hidden', String(pageName !== 'shop'));
        tertiaryPage?.setAttribute('aria-hidden', String(pageName !== 'tertiary'));
      };

      const queueFocus = (element) => {
        window.clearTimeout(focusTimer);
        if (!(element instanceof HTMLElement)) return;
        focusTimer = window.setTimeout(() => element.focus({ preventScroll: true }), LEVEL_FOCUS_MS);
      };

      const clearTertiary = () => {
        activeTertiaryKey = '';
        tertiaryTriggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
        tertiaryGroups.forEach((group) => {
          group.classList.remove('is-active');
          group.setAttribute('aria-hidden', 'true');
        });
      };

      const resetPrimary = ({ animate = false } = {}) => {
        clearTimers();
        drawer.classList.remove(
          'is-shop-page',
          'is-tertiary-page',
          'is-primary-exiting',
          'is-shop-exiting',
          'is-shop-overlap',
          'is-primary-overlap',
          'is-primary-revealing',
          'is-shop-revealing',
          'is-tertiary-revealing'
        );
        setPageAria('primary');
        clearTertiary();

        if (animate) restartReveal('is-primary-revealing');
      };

      const showPrimary = ({ focus = false, animate = Boolean(menu.open) } = {}) => {
        if (!drawer.classList.contains('is-shop-page')) {
          resetPrimary({ animate });
          if (focus) queueFocus(shopTrigger);
          return;
        }

        clearTimers();
        drawer.classList.remove('is-primary-exiting', 'is-shop-overlap', 'is-tertiary-page');
        drawer.classList.add('is-shop-exiting');
        clearTertiary();

        overlapTimer = window.setTimeout(() => {
          drawer.classList.add('is-primary-overlap');
          primaryPage.setAttribute('aria-hidden', 'false');
          if (animate) restartReveal('is-primary-revealing');
        }, LEVEL_OVERLAP_MS);

        switchTimer = window.setTimeout(() => {
          drawer.classList.remove(
            'is-shop-exiting',
            'is-primary-overlap',
            'is-shop-page',
            'is-shop-revealing'
          );
          setPageAria('primary');
        }, LEVEL_SWITCH_MS);

        if (focus) queueFocus(shopTrigger);
      };

      const showShop = ({ focus = false, animate = true } = {}) => {
        if (drawer.classList.contains('is-tertiary-page')) {
          clearTimers();
          drawer.classList.remove('is-tertiary-page');
          drawer.classList.add('is-shop-page');
          setPageAria('shop');
          tertiaryTriggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
          if (animate) restartReveal('is-shop-revealing');

          if (focus) {
            const previousTrigger = tertiaryTriggers.find(
              (trigger) => trigger.dataset.kvMobileTertiaryTrigger === activeTertiaryKey
            );
            queueFocus(previousTrigger || shopBack);
          }
          return;
        }

        if (
          drawer.classList.contains('is-shop-page') ||
          drawer.classList.contains('is-primary-exiting')
        ) {
          return;
        }

        clearTimers();
        drawer.classList.remove('is-shop-exiting', 'is-primary-overlap');
        drawer.classList.add('is-primary-exiting');

        overlapTimer = window.setTimeout(() => {
          drawer.classList.add('is-shop-overlap');
          shopPage.setAttribute('aria-hidden', 'false');
          if (animate) restartReveal('is-shop-revealing');
        }, LEVEL_OVERLAP_MS);

        switchTimer = window.setTimeout(() => {
          drawer.classList.remove(
            'is-primary-exiting',
            'is-shop-overlap',
            'is-primary-revealing'
          );
          drawer.classList.add('is-shop-page');
          setPageAria('shop');
        }, LEVEL_SWITCH_MS);

        if (focus) queueFocus(shopBack);
      };

      const showTertiary = (key, { focus = true } = {}) => {
        if (!hasTertiary) return;

        const group = tertiaryGroups.find((candidate) => candidate.dataset.kvMobileTertiaryGroup === key);
        const trigger = tertiaryTriggers.find((candidate) => candidate.dataset.kvMobileTertiaryTrigger === key);
        if (!group || !trigger || !tertiaryBack || !tertiaryBackLabel) return;

        clearTimers();
        activeTertiaryKey = key;

        tertiaryTriggers.forEach((candidate) => {
          candidate.setAttribute('aria-expanded', String(candidate === trigger));
        });

        tertiaryGroups.forEach((candidate) => {
          const isActive = candidate === group;
          candidate.classList.toggle('is-active', isActive);
          candidate.setAttribute('aria-hidden', String(!isActive));
        });

        const label = trigger.querySelector('span')?.textContent?.trim() || 'Shop';
        tertiaryBackLabel.textContent = label;

        drawer.classList.add('is-shop-page', 'is-tertiary-page');
        setPageAria('tertiary');
        restartReveal('is-tertiary-revealing');

        if (focus) queueFocus(tertiaryBack);
      };

      shopTrigger.addEventListener('click', () => showShop({ focus: true, animate: true }));
      shopBack.addEventListener('click', () => showPrimary({ focus: true, animate: true }));
      if (hasTertiary && tertiaryBack) {
        tertiaryBack.addEventListener('click', () => showShop({ focus: true, animate: true }));

        tertiaryTriggers.forEach((trigger) => {
          trigger.addEventListener('click', () => {
            showTertiary(trigger.dataset.kvMobileTertiaryTrigger);
          });
        });
      }

      menu.addEventListener('toggle', () => {
        if (!menu.open) {
          resetPrimary({ animate: false });
          return;
        }

        resetPrimary({ animate: true });
      });

      resetPrimary({ animate: false });
    });
  };

  initDrawerLevels();
  document.addEventListener('shopify:section:load', (event) => initDrawerLevels(event.target));
  window.addEventListener('pageshow', () => initDrawerLevels());
})();
